import React, { useMemo, useState } from 'react';
import {
  ArrowRight, Check, CheckCircle2, ChevronDown, ChevronsRight, ChevronUp, Circle, CircleCheck, Cog, Eye, Factory,
  GitBranch, Package, Split, Truck, X,
} from 'lucide-react';

import { formatIST } from '@/lib/utils';
import { DEPT_NAMES } from '@/lib/constants';
import { PRIORITY_MAP } from '@/lib/status-colors';
import { divideBatch } from '@/lib/api';
import { toast } from 'sonner';
import { evenSizes, parseSizes } from '@/components/DivideBatchStrip';
import BatchHistoryPanel from '../OrderDetail/BatchHistoryPanel';
import { INPUT_CLASS, inputStyle } from '@/components/phone/FieldControls';
import {
  BottomSheet, EmptyState, PageHeader, PhonePage, PrimaryButton, ScreenLoader, SearchBar, StatusPill,
} from '@/components/phone/primitives';
import { Colors, pcs, priorityTone } from '@/components/phone/theme';

/**
 * The floor, on a phone.
 *
 * The desktop is a flat grid of every batch with a strip that opens under a row;
 * a phone keeps the flat list and makes each batch a card carrying its own
 * buttons. The fifty-four stages that run across the top of the desktop open
 * as one sheet, grouped the way the floor is laid out, because a strip that
 * long is a minute of thumb-scrolling. Closing a stage and dividing a batch
 * open sheets at the thumb; ticking batches raises a bar at the bottom with
 * the one button that moves them on.
 */

const BATCH_TONE = { pending: 'neutral', in_progress: 'accent', completed: 'positive' };

function Field({ label, name, value, onChange, numeric, placeholder }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] font-medium" style={{ color: Colors.text.secondary }}>{label}</span>
      <input
        data-testid={`field-${name}`}
        className={INPUT_CLASS}
        style={inputStyle}
        value={value}
        onChange={(e) => onChange(numeric ? e.target.value.replace(/\D/g, '') : e.target.value)}
        onFocus={numeric ? (e) => e.target.select() : undefined}
        inputMode={numeric ? 'numeric' : undefined}
        placeholder={placeholder || (numeric ? '0' : '')}
        aria-label={label}
      />
    </label>
  );
}

/** Every stage on the floor as one sheet, grouped the way the bar groups them. */
function StagePicker({ stagesByCategory, selectedStage, total, onSelect }) {
  const [open, setOpen] = useState(false);
  const pick = (name) => { onSelect(name); setOpen(false); };
  const Row = ({ name, count, label, testID }) => {
    const on = selectedStage === name;
    return (
      <button
        type="button"
        data-testid={testID}
        onClick={() => pick(name)}
        className="flex min-h-[44px] w-full items-center justify-between gap-3 border-b px-1 py-2.5 text-left last:border-b-0"
        style={{ borderColor: Colors.border.subtle }}
      >
        <span
          className="truncate text-[15px]"
          style={{ color: on ? Colors.accent.DEFAULT : count > 0 ? Colors.text.primary : Colors.text.tertiary, fontWeight: on ? 700 : 400 }}
        >
          {label}
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {count > 0 && <StatusPill label={String(count)} tone={on ? 'accent' : 'neutral'} />}
          {on && <Check className="h-[18px] w-[18px]" style={{ color: Colors.accent.DEFAULT }} />}
        </span>
      </button>
    );
  };
  return (
    <>
      <button
        type="button"
        data-testid="stage-picker-btn"
        onClick={() => setOpen(true)}
        className="flex h-12 w-full items-center justify-between gap-2 rounded-[10px] border px-3 text-left"
        style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
      >
        <span className="truncate text-[14px] font-semibold" style={{ color: Colors.text.primary }}>
          {selectedStage || 'All stages'}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0" style={{ color: Colors.text.secondary }} />
      </button>
      <BottomSheet open={open} onClose={() => setOpen(false)} title="Which stage?" testID="stage-sheet">
        <div className="max-h-[24rem] overflow-y-auto">
          <Row name={null} count={total} label="All stages" testID="stage-option-all" />
          {Object.entries(stagesByCategory).map(([category, group]) => (
            <div key={category}>
              <p className="pb-1 pt-3 text-[10px] font-semibold uppercase tracking-[1px]" style={{ color: Colors.text.tertiary }}>
                {category}
              </p>
              {group.map((stage) => (
                <Row key={stage.name} name={stage.name} count={stage.count} label={stage.name}
                  testID={`stage-option-${stage.name}`} />
              ))}
            </div>
          ))}
        </div>
      </BottomSheet>
    </>
  );
}

function CardAction({ icon: Icon, label, onClick, disabled, tone = 'neutral', testID }) {
  const fill = tone === 'positive' ? Colors.semantic.positive : tone === 'accent' ? Colors.accent.DEFAULT : null;
  return (
    <button
      type="button"
      data-testid={testID}
      onClick={onClick}
      disabled={disabled}
      className="flex h-11 shrink-0 items-center gap-1.5 rounded-[10px] border px-3 text-[13px] font-semibold disabled:opacity-50"
      style={fill
        ? { backgroundColor: fill, borderColor: fill, color: Colors.text.inverse }
        : { backgroundColor: Colors.background.primary, borderColor: Colors.border.default, color: Colors.text.primary }}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}

function BatchCard({
  row, open, onToggle, canBatchProcess, picked, onPick, assignment,
  onComplete, onDivide, onAssign, onPlateOrdered, onPlateReceived, plateBusy, onOpenOrder,
}) {
  const { batch, item, order } = row;
  const movable = batch.status === 'in_progress' && !row.atPlateMaking;
  const finished = batch.status === 'completed';
  const stages = item.production_stages || [];
  const PickIcon = picked ? CircleCheck : Circle;

  return (
    <li
      data-testid={`batch-card-${batch.id}`}
      className="mt-3 rounded-[10px] border"
      style={{
        backgroundColor: picked ? Colors.accent.wash : Colors.background.primary,
        borderColor: picked ? Colors.accent.DEFAULT : Colors.border.default,
      }}
    >
      <div className="flex items-start gap-2 px-3 pt-3">
        {canBatchProcess && movable && (
          <button
            type="button"
            data-testid={`pick-batch-${batch.id}`}
            onClick={onPick}
            aria-pressed={picked}
            aria-label={`Select batch #${batch.batch_number} of ${item.item_name || order.order_id}`}
            className="flex h-11 w-11 shrink-0 items-center justify-center"
          >
            <PickIcon className="h-6 w-6" style={{ color: picked ? Colors.accent.DEFAULT : Colors.text.disabled }} />
          </button>
        )}
        <button
          type="button"
          onClick={onToggle}
          data-testid={`batch-toggle-${batch.id}`}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-start gap-2 text-left"
        >
          <span className="min-w-0 flex-1">
            <span className="line-clamp-2 block text-[15px] font-bold -tracking-[0.2px]" style={{ color: Colors.text.primary }}>
              {item.item_name || item.name || item.product_subtype || 'Item'}
            </span>
            <span className="mt-0.5 block truncate text-[12px] font-medium" style={{ color: Colors.text.secondary }}>
              {order.customer_name}{order.firm_name ? ` — ${order.firm_name}` : ''}
            </span>
            <span className="mt-0.5 block truncate text-[11px]" style={{ color: Colors.text.tertiary }}>
              {order.order_id} · {DEPT_NAMES[item.order_type] || item.order_type} · Batch #{batch.batch_number} · {pcs(batch.quantity)}
            </span>
          </span>
          {open
            ? <ChevronUp className="mt-1 h-[18px] w-[18px] shrink-0" style={{ color: Colors.text.tertiary }} />
            : <ChevronDown className="mt-1 h-[18px] w-[18px] shrink-0" style={{ color: Colors.text.tertiary }} />}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 px-4 pb-3 pt-2">
        <StatusPill
          label={finished ? 'Completed' : batch.current_stage_name}
          tone={BATCH_TONE[batch.status] || 'neutral'}
          dot
          testID={`batch-stage-${batch.id}`}
        />
        {(batch.split_from || batch.has_splits) && (
          <StatusPill label={batch.split_from ? `From #${batch.split_from.parent_batch_number}` : 'Split'} tone="caution" />
        )}
        {row.plateRequired && !finished && (
          <StatusPill label={`Plate ${row.plateStatus}`} tone={row.plateStatus === 'received' ? 'positive' : 'caution'} />
        )}
        {assignment?.machine_name && <StatusPill label={assignment.machine_name} tone="neutral" />}
        {order.priority && order.priority !== 'medium' && (
          <StatusPill label={PRIORITY_MAP[order.priority]?.label || order.priority} tone={priorityTone(order.priority)} />
        )}
        {!!order.expected_delivery_date && (
          <span className="text-[11px]" style={{ color: Colors.text.tertiary }}>
            Due {formatIST(order.expected_delivery_date, { dateOnly: true })}
          </span>
        )}
      </div>

      {open && (
        <div className="border-t" style={{ borderColor: Colors.border.subtle }} data-testid={`batch-detail-${batch.id}`}>
          {stages.length > 0 && (
            <div className="px-4 py-3">
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[1px]" style={{ color: Colors.text.tertiary }}>Pipeline</p>
              <div className="flex flex-wrap items-center gap-1">
                {stages.map((stage, si) => {
                  const here = si === (batch.current_stage_index || 0);
                  const done = si < (batch.current_stage_index || 0);
                  return (
                    <React.Fragment key={si}>
                      <StatusPill label={stage.stage_name} tone={done ? 'positive' : here ? 'accent' : 'neutral'} />
                      {si < stages.length - 1 && <ArrowRight className="h-3 w-3 shrink-0" style={{ color: Colors.text.disabled }} />}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}
          <BatchHistoryPanel batch={batch} />
        </div>
      )}

      <div
        className="flex gap-2 overflow-x-auto border-t px-3 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{ borderColor: Colors.border.subtle }}
      >
        {row.atPlateMaking ? (
          <>
            {row.plateStatus !== 'ordered' && row.plateStatus !== 'received' && (
              <CardAction icon={Package} label="Plate ordered" disabled={plateBusy} onClick={onPlateOrdered}
                testID={`plate-ordered-${batch.id}`} />
            )}
            {row.plateStatus !== 'received' && (
              <CardAction icon={Truck} label="Plate received" tone="positive" disabled={plateBusy} onClick={onPlateReceived}
                testID={`plate-received-${batch.id}`} />
            )}
          </>
        ) : batch.status === 'in_progress' && (
          <CardAction icon={CheckCircle2} label="Complete" tone="positive" onClick={onComplete}
            testID={`complete-batch-${batch.id}`} />
        )}
        {!finished && !row.atPlateMaking && (
          <CardAction icon={Cog} label={assignment ? 'Machine' : 'Assign'} tone={assignment ? 'neutral' : 'accent'}
            onClick={onAssign} testID={`assign-batch-${batch.id}`} />
        )}
        {canBatchProcess && !finished && batch.quantity > 1 && (
          <CardAction icon={Split} label="Divide" onClick={onDivide} testID={`divide-batch-${batch.id}`} />
        )}
        <CardAction icon={Eye} label="Order" onClick={onOpenOrder} />
      </div>
    </li>
  );
}

/** Cutting a batch into runs, with the same arithmetic the desktop strip uses. */
function DivideSheet({ row, onCancel, onDone }) {
  const batch = row?.batch;
  const total = batch?.quantity || 0;
  const [count, setCount] = useState('2');
  const [sizesText, setSizesText] = useState(() => evenSizes(total, 2).join(', '));
  const [saving, setSaving] = useState(false);

  const sizes = useMemo(() => parseSizes(sizesText), [sizesText]);
  const sum = sizes.reduce((s, n) => s + n, 0);
  const balanced = sizes.length >= 2 && sum === total && sizes.every((n) => n > 0);

  const setCountAndSizes = (raw) => {
    setCount(raw);
    const even = evenSizes(total, parseInt(raw, 10));
    if (even.length) setSizesText(even.join(', '));
  };

  const submit = async () => {
    setSaving(true);
    try {
      await divideBatch(batch.id, { quantities: sizes });
      toast.success(`Batch #${batch.batch_number} divided into ${sizes.length} runs`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to divide the batch');
    }
    setSaving(false);
  };

  return (
    <BottomSheet
      open={!!row}
      onClose={onCancel}
      title={batch ? `Divide batch #${batch.batch_number}` : ''}
      subtitle={batch ? `${pcs(total)} at ${batch.current_stage_name}` : ''}
      testID="divide-sheet"
    >
      <div className="grid grid-cols-[6rem_1fr] gap-3">
        <Field label="Runs" name="run_count" value={count} onChange={setCountAndSizes} numeric />
        <label className="block">
          <span className="mb-1.5 block text-[11px] font-medium" style={{ color: Colors.text.secondary }}>Quantities</span>
          <input
            className={INPUT_CLASS}
            style={inputStyle}
            value={sizesText}
            onChange={(e) => setSizesText(e.target.value)}
            aria-label="Run quantities"
            inputMode="numeric"
          />
        </label>
      </div>
      <p className="mt-3 text-[12px]" style={{ color: balanced ? Colors.text.secondary : Colors.semantic.caution }}>
        {balanced
          ? `${sizes.length} runs, each carrying the work already done.`
          : sizes.length < 2
            ? 'Give at least two quantities.'
            : `The runs add up to ${sum.toLocaleString()} — the batch holds ${total.toLocaleString()}.`}
      </p>
      <PrimaryButton className="mt-4" onClick={submit} disabled={!balanced} busy={saving} testID="confirm-divide-batch-btn">
        <Split className="h-4 w-4" /> Divide
      </PrimaryButton>
    </BottomSheet>
  );
}

export default function PhoneBoard({
  stagesByCategory, selectedStage, onStage, stats, totalPending,
  rows, loading, onSearch, searchQuery,
  expandedId, onToggle, assignmentOf,
  canBatchProcess, picked, movableRows, pickedRows, pickedPieces, allPicked,
  onTogglePick, onPickAll, onClearPicked, onAdvance, advancing,
  completing, form, onFormChange, onStartComplete, onCancelComplete, onSubmitComplete, submitting,
  dividing, onStartDivide, onCancelDivide, onDivided,
  onAssign, plateSubmitting, onPlateOrdered, onPlateReceived, onOpenOrder,
}) {
  const completingRow = rows.find((r) => r.id === completing);
  const dividingRow = rows.find((r) => r.id === dividing);
  const qty = parseInt(form.qty_completed, 10) || 0;
  const willSplit = completingRow && qty > 0 && qty < completingRow.batch.quantity;

  return (
    <PhonePage testID="production-page">
      <PageHeader
        title="Production"
        subtitle={`${rows.length} batch${rows.length === 1 ? '' : 'es'} listed · ${totalPending} active on the floor`}
      />

      <div className="px-5">
        <SearchBar placeholder="Search orders…" onChange={onSearch} testID="production-search" />
        <div className="mt-3">
          <StagePicker stagesByCategory={stagesByCategory} selectedStage={selectedStage} total={stats.total} onSelect={onStage} />
        </div>
        {canBatchProcess && movableRows.length > 0 && (
          <button
            type="button"
            data-testid="pick-all-batches"
            onClick={onPickAll}
            className="mt-2 flex h-10 items-center gap-1.5 text-[12px] font-semibold"
            style={{ color: Colors.accent.DEFAULT }}
          >
            {allPicked ? <CircleCheck className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
            {allPicked ? 'Clear selection' : `Select all ${movableRows.length} movable`}
          </button>
        )}
      </div>

      <div className={`flex-1 px-5 ${pickedRows.length ? 'pb-28' : 'pb-8'}`}>
        {loading ? (
          <ScreenLoader />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Factory}
            message={searchQuery ? 'No orders match your search'
              : selectedStage ? `Nothing at "${selectedStage}"` : 'Nothing in production'}
          />
        ) : (
          <ul data-testid="batch-list">
            {rows.map((row) => (
              <BatchCard
                key={row.id}
                row={row}
                open={expandedId === row.id}
                onToggle={() => onToggle(row)}
                canBatchProcess={canBatchProcess}
                picked={picked.has(row.id)}
                onPick={() => onTogglePick(row.id)}
                assignment={assignmentOf(row.batch)}
                onComplete={() => onStartComplete(row)}
                onDivide={() => onStartDivide(row)}
                onAssign={() => onAssign(row)}
                onPlateOrdered={() => onPlateOrdered(row)}
                onPlateReceived={() => onPlateReceived(row)}
                plateBusy={plateSubmitting === row.item.id}
                onOpenOrder={() => onOpenOrder(row)}
              />
            ))}
          </ul>
        )}
      </div>

      {/* the ticked batches, at the thumb — it only exists once something is ticked */}
      {pickedRows.length > 0 && (
        <div
          data-testid="batch-selection-bar"
          className="fixed bottom-4 left-1/2 z-30 flex w-[calc(100%-2.5rem)] max-w-[23.5rem] -translate-x-1/2 items-center gap-3 rounded-[14px] px-3 py-2.5 shadow-lg"
          style={{ backgroundColor: Colors.neutral[800] }}
        >
          <button
            type="button"
            onClick={onClearPicked}
            aria-label="Clear batch selection"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10"
          >
            <X className="h-[18px] w-[18px]" style={{ color: Colors.text.inverse }} />
          </button>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[12px] font-semibold" style={{ color: Colors.text.inverse }}>
              {pickedRows.length} batch{pickedRows.length === 1 ? '' : 'es'}
            </p>
            <p className="truncate text-[11px]" style={{ color: Colors.neutral[300] }}>{pcs(pickedPieces)}</p>
          </div>
          <button
            type="button"
            data-testid="advance-picked-batches"
            onClick={onAdvance}
            disabled={advancing}
            className="flex shrink-0 items-center gap-1.5 rounded-[10px] px-3 py-2 text-[13px] font-bold disabled:opacity-60"
            style={{ backgroundColor: Colors.accent.DEFAULT, color: Colors.text.inverse }}
          >
            <ChevronsRight className="h-[18px] w-[18px]" />
            {advancing ? 'Advancing…' : 'Advance'}
          </button>
        </div>
      )}

      <BottomSheet
        open={!!completingRow}
        onClose={onCancelComplete}
        title={completingRow ? `Close ${completingRow.batch.current_stage_name}` : ''}
        subtitle={completingRow ? `${completingRow.item.item_name || completingRow.order.order_id} · Batch #${completingRow.batch.batch_number} · ${pcs(completingRow.batch.quantity)}` : ''}
        testID="complete-sheet"
      >
        <div className="grid grid-cols-2 gap-3">
          <Field label="Done" name="qty_completed" value={form.qty_completed} onChange={(v) => onFormChange('qty_completed', v)} numeric />
          <Field label="Rejected" name="qty_rejected" value={form.qty_rejected} onChange={(v) => onFormChange('qty_rejected', v)} numeric />
          <Field label="Operator" name="operator" value={form.operator} onChange={(v) => onFormChange('operator', v)} />
          <Field label="Machine" name="machine_used" value={form.machine_used} onChange={(v) => onFormChange('machine_used', v)} />
        </div>
        <div className="mt-3">
          <Field label="Remarks" name="remarks" value={form.remarks} onChange={(v) => onFormChange('remarks', v)} placeholder="Optional" />
        </div>
        {willSplit && (
          <p className="mt-3 flex items-start gap-1.5 text-[12px]" style={{ color: Colors.semantic.caution }} data-testid="split-warning">
            <GitBranch className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            Batch will split: {qty} advances, {completingRow.batch.quantity - qty} stays at {completingRow.batch.current_stage_name}.
          </p>
        )}
        <PrimaryButton className="mt-4" onClick={() => onSubmitComplete(completingRow)} busy={submitting} testID="confirm-complete-stage-btn">
          Done
        </PrimaryButton>
      </BottomSheet>

      {/* keyed so a fresh batch starts from an even split, not the last one's typing */}
      <DivideSheet key={dividing || 'none'} row={dividingRow} onCancel={onCancelDivide} onDone={onDivided} />
    </PhonePage>
  );
}
